import { migrateTraversal } from "../helpers/migrateTraversal";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";

type TMigratePosition = {
  x: number;
  y: number;
}

type TMigrateDimensions = {
  width: number;
  height: number;
}

type TMigrateLayoutDataV2Result = {
  position: TMigratePosition;
  dimensions: TMigrateDimensions;
}

type TMigrateLayoutV2Result = {
  desktop: TMigrateLayoutDataV2Result;
}

export interface IMigrateElementV1Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    position: TMigratePosition;
    dimensions: TMigrateDimensions;
  }
}

export interface IMigrateElementV2Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    layout: TMigrateLayoutV2Result;
  }
}

function isMigrateElementV1Input(node: any): node is IMigrateElementV1Input {  
  const partialNode: Partial<IMigrateElementV1Input> = node;
  
  return (
    partialNode?.type === "element" &&
    partialNode.element?.position?.x != null &&
    partialNode.element?.position?.y != null &&
    partialNode.element?.dimensions?.width != null &&
    partialNode.element?.dimensions?.height != null
  );
}

export interface IMigrateContainerV1Input extends IWebsiteBuilderMigrationNode {
  type: "container";
  container: {
    position: TMigratePosition;
    dimensions: TMigrateDimensions;
  }
  children: IWebsiteBuilderMigrationNode[];
}

export interface IMigrateContainerV2Result extends IWebsiteBuilderMigrationNode {
  type: "container";
  container: {
    layout: TMigrateLayoutV2Result;
  }
  children: IWebsiteBuilderMigrationNode[];
}

function isMigrateContainerV1Input(node: any): node is IMigrateContainerV1Input {
  const partialNode: Partial<IMigrateContainerV1Input> = node;

  return (
    partialNode?.type === "container" &&
    partialNode.container?.position?.x != null &&
    partialNode.container?.position?.y != null &&
    partialNode.container?.dimensions?.width != null &&
    partialNode.container?.dimensions?.height != null
  );
}

export function migrateSchemaV1ToV2(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 1) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 2,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    if (prev?.type === "element") {
      if (!isMigrateElementV1Input(prev)) {
        throw new WebsiteBuilderMigrationError({
          type: "element-missing-field",
          schemaVersion: 1,
          nextSchemaVersion: 2,
        });
      }
      
      const elementBaseV2Result: IMigrateElementV1Input = cloneDeep(prev);

      const elementLayoutV2Result: TMigrateLayoutV2Result = {
        desktop: {
          position: { ...elementBaseV2Result.element.position },
          dimensions: { ...elementBaseV2Result.element.dimensions },
        },
      };

      delete elementBaseV2Result.element.position;
      delete elementBaseV2Result.element.dimensions;

      const elementV2Result: IMigrateElementV2Result = {
        ...elementBaseV2Result,
        element: {
          ...elementBaseV2Result.element,
          layout: elementLayoutV2Result,
        },
      };
      
      return elementV2Result;
    } else if (prev?.type === "container") {
      if (!isMigrateContainerV1Input(prev)) {
        throw new WebsiteBuilderMigrationError({
          type: "container-missing-field",
          schemaVersion: 1,
          nextSchemaVersion: 2,
        });
      }
      
      const containerBaseV2Result: IMigrateContainerV1Input = cloneDeep(prev);

      const containerLayoutV2Result: TMigrateLayoutV2Result = {
        desktop: {
          position: { ...containerBaseV2Result.container.position },
          dimensions: { ...containerBaseV2Result.container.dimensions },
        },
      };

      delete containerBaseV2Result.container.position;
      delete containerBaseV2Result.container.dimensions;

      const containerV2Result: IMigrateContainerV2Result = {
        ...containerBaseV2Result,
        container: {
          ...containerBaseV2Result.container,
          layout: containerLayoutV2Result,
        },
      };

      return containerV2Result;
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 2,
  };
}